import styled from "styled-components";
import { borderRadius, spacing, typographyMixin } from "@magicbi/ui-library";
import { rawColors } from "../../../design-system/tokens/colors";
import { borderRadius as localRadius } from "../../../design-system/tokens/borders";
import { fontFamilies } from "../../../design-system/tokens/typography";
import { spacing as localSpacing } from "../../../design-system/tokens/spacing";

/** The chart list's width (F26: 320px). GAP: no width token. */
const LIST_WIDTH = "20rem";

export const Layout = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  min-width: 0;
`;

export const ListPane = styled.aside`
  display: flex;
  flex: none;
  flex-direction: column;
  width: ${LIST_WIDTH};
  min-height: 0;
  background: ${rawColors.structural.themeLight};
  border-right: 1px solid ${({ theme }) => theme.colors.border.stroke};
`;

export const ListTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  padding: ${spacing[3]};
`;

export const SearchInput = styled.input`
  /* F26: 34px. */
  height: calc(${localSpacing[8]} + ${localSpacing[0.5]});
  padding: 0 ${spacing[2.5]};
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.surface.primary};
  border: 1px solid ${rawColors.stroke.strong};
  border-radius: ${localRadius[10]};
`;

export const CompareRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Spacer = styled.span`
  flex: 1;
`;

export const GroupHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[1.5]};
  padding: ${spacing[2]} ${spacing[3]} ${spacing[1]};
  ${typographyMixin("caption", 2)}
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** A group's title inside the chart list (by page or by tag). */
export const GroupTitle = styled.li`
  padding: ${spacing[2]} ${spacing[1]} ${spacing[1]};
  ${typographyMixin("caption", 2)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Muted = styled.span`
  color: ${rawColors.text.lowEmphasis};
`;

export const ChartList = styled.ul`
  flex: 1;
  min-height: 0;
  margin: 0;
  padding: 0 ${spacing[2]} ${spacing[3]};
  overflow: auto;
  list-style: none;
`;

export const ChartRow = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing[2.5]};
  width: 100%;
  height: ${spacing[10]};
  padding: 0 ${spacing[2.5]};
  /* GAP: the design's 13px has no type token; body 4 (14px) is the nearest, bold when picked. */
  ${({ $active }) => ($active ? typographyMixin("body", 1) : typographyMixin("body", 4))}
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: left;
  background: ${({ theme, $active }) => ($active ? theme.colors.surface.primary : "transparent")};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.text.interactive : "transparent")};
  border-radius: ${localRadius[10]};
  cursor: pointer;
`;

/** A text item: no query, greyed, not a button. */
export const TextRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2.5]};
  width: 100%;
  height: ${spacing[10]};
  padding: 0 ${spacing[2.5]};
  box-sizing: border-box;
  ${typographyMixin("body", 4)}
  color: ${rawColors.text.subtleEmphasis};
  cursor: not-allowed;
`;

/** The 24px kind tile (radius 7). */
export const KindIcon = styled.span<{ $bg: string; $fg: string }>`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: ${spacing[6]};
  height: ${spacing[6]};
  border-radius: ${localRadius[7]};
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
`;

export const RowTitle = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RowKind = styled.span`
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Detail = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${spacing[3]};
  min-width: 0;
  min-height: 0;
  padding: ${spacing[4]} ${spacing[5]};
  overflow: auto;
`;

export const DetailHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2.5]};
`;

export const DetailTitle = styled.h3`
  margin: 0;
  ${typographyMixin("body", 3)}
`;

export const Panes = styled.div<{ $columns: number }>`
  display: grid;
  flex: 1;
  min-height: 0;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  gap: ${spacing[3]};
`;

/** The disabled security strip: Soon tag and three chips. */
export const DisabledBlock = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[2]};
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.lowEmphasis};
  cursor: not-allowed;
  opacity: 0.55;
`;

/** The "PARAMETERS" label ahead of the parameter chips: 11/700, caps, tracked. */
export const SecurityLabel = styled.span`
  ${typographyMixin("overline", 4)}
  /* GAP: design 11px / .06em has no token; 0.6875rem and 0.06em written directly. */
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${rawColors.text.lowEmphasis};
`;

/** F26 security chip: 26px, 11px/600. `tone` picks peach, teal or violet. */
export const SecurityChip = styled.span<{ $tone: "params" | "rls" | "masked" }>`
  display: inline-flex;
  align-items: center;
  height: calc(${localSpacing[6]} + ${localSpacing[0.5]});
  padding: 0 ${spacing[2.5]};
  border-radius: ${localRadius.full};
  ${typographyMixin("caption", 2)}
  ${({ $tone }) =>
    $tone === "params"
      ? `background: ${rawColors.accent.drill.bg}; color: ${rawColors.accent.drill.text};`
      : $tone === "rls"
        ? `background: ${rawColors.accent.cross.bg}; color: ${rawColors.accent.cross.text};`
        : `background: ${rawColors.primary[100]}; color: ${rawColors.primary[700]};`}
`;

/** The title line over the panes: chart title, kind chip, layout switch. */
export const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2.5]};
`;

export const TopTitle = styled.h3`
  margin: 0;
  ${typographyMixin("body", 1)}
  color: ${rawColors.text.highEmphasis};
`;

export const KindChip = styled.span`
  display: inline-flex;
  align-items: center;
  height: ${spacing[6]};
  padding: 0 ${spacing[2.5]};
  border-radius: ${localRadius.full};
  background: ${rawColors.structural[100]};
  color: ${rawColors.text.strongEmphasis};
  ${typographyMixin("caption", 2)}
`;

/** A dark SQL pane (F26): 40px header, then the code. */
export const PaneBox = styled.div<{ $muted?: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  border-radius: ${localRadius[14]};
  background: ${rawColors.brand.primaryDarker};
  ${({ $muted }) => ($muted ? "opacity: 0.5; cursor: not-allowed;" : "")}
`;

export const PaneHead = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  gap: ${spacing[2.5]};
  height: ${spacing[10]};
  padding: 0 ${spacing[2.5]} 0 calc(${localSpacing[3]} + ${localSpacing[0.5]});
  border-bottom: 1px solid ${rawColors.text.strongEmphasis};
  box-sizing: border-box;
`;

export const PaneTag = styled.span<{ $engine?: boolean }>`
  display: inline-flex;
  align-items: center;
  height: calc(${localSpacing[5]} + ${localSpacing[0.5]});
  padding: 0 ${spacing[2.5]};
  border-radius: ${localRadius.full};
  background: ${({ $engine }) => ($engine ? rawColors.secondary[700] : rawColors.text.strongEmphasis)};
  color: ${({ $engine }) => ($engine ? rawColors.text.white : rawColors.stroke[200])};
  ${typographyMixin("caption", 2)}
`;

export const PaneTitle = styled.b`
  ${typographyMixin("caption", 1)}
  color: ${rawColors.text.white};
`;

export const PaneHint = styled.small`
  ${typographyMixin("overline", 4)}
  color: ${rawColors.text.subtleEmphasis};
`;

/** GAP: design pane-button bg #241C35 has no colour token. */
const PANE_BUTTON_BG = "#241C35";

/** 26px Copy button, radius 7, on a lifted dark. */
export const PaneButton = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  height: calc(${localSpacing[6]} + ${localSpacing[0.5]});
  padding: 0 ${spacing[2.5]};
  border-radius: ${localRadius[7]};
  background: ${PANE_BUTTON_BG};
  color: ${rawColors.grey[200]};
  ${typographyMixin("overline", 4)}
  font-weight: 600;
  cursor: pointer;

  &[aria-disabled="true"] {
    cursor: not-allowed;
    color: ${rawColors.text.lowEmphasis};
  }

  &:focus-visible {
    outline: 2px solid ${rawColors.primary[200]};
  }
`;

export const PaneCode = styled.pre`
  flex: 1;
  min-height: 0;
  margin: 0;
  padding: ${spacing[3]} ${spacing[4]};
  overflow: auto;
  color: ${rawColors.stroke[200]};
  ${typographyMixin("caption", 4)}
  font-family: ${fontFamilies.mono};
  white-space: pre;
`;

export const Note = styled.p`
  margin: 0;
  ${typographyMixin("body", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;
